/**
 * 69.30 steps 1–3 and 5 (ARCHITECTURE §5.26; DECISIONS 248), with GameConfig.munitionsSupply: ammunition by its own type at the
 * dumps, and a vehicle's rearm parked near one.
 * - **The dumps** are crates (`supply.ammo`, a count per round type): each side's base crates and its FOBs' start with a
 *   fixed amount (MUNITIONS.base, .fob — 248 (4): nothing tops them up yet). Any standing crate with rounds is a dump, so
 *   one laid in play (the utility drone's drop, 69.32) is one as well; open to every side (242).
 * - **The stores** a vehicle rearms (a store's start is its cap): the tank's main gun (its explosive and solid rounds, the
 *   one in the gun counted with its kind; cap the stowage dial), each mounted launcher's rocket or anti-dropship missile (the
 *   tubes' and the store's; cap a magazine and the store dial), the mortar carrier's bombs (cap the mortar's store dial).
 * - **The rearm** (248 (2)): a standing vehicle parked (under MUNITIONS.stopped of the walk's speed) within MUNITIONS.reach
 *   of a dump holding a round it is short of takes one at that type's pace (MUNITIONS.pace) — the type furthest below its
 *   cap, the first store on a tie — into its store (the tank's stowage, the launcher's `spare`, the mortar's rounds), the
 *   dump's count down by one. Its seconds toward the next round are the vehicle's `rearm`, gone when it drives off or is
 *   full. Nobody need be aboard; a dry gun or launcher loads it as any reload (match.ts).
 *
 * - **The salvo's shells** (step 3, 248 (3)): a side's strike salvo is paid in light shells off its own base crates
 *   (`baseShells`, `spendBaseShells`; the strike's count retired — core/airstrike.ts).
 * - **The supply truck's bed, a dump that drives** (step 5): standing at its home (the convoys' `atHome`), a supply truck
 *   tops its bed's rounds (`StructureState.ammo`) up to MUNITIONS.truck by type from its side's base crates (`loadBeds`, as
 *   far as they go; never the salvo's light shells); parked anywhere, a vehicle parked within MUNITIONS.truckReach of it
 *   rearms from its bed as from a crate (any side's, 242). A wrecked truck's bed is no dump.
 *
 * Pure; the Match steps it on its beat (RATES.rearm). Nothing here runs, and nothing is written, with the switch off.
 */
import type { GameState, GameConfig, StructureState, AmmoType, TankRound } from '../types';
import { MUNITIONS, TANK_GUN, MOUNT_LAUNCHERS, MORTAR } from '../constants';
import { standingWithJob, cargoOnly, drives } from './body';
import { arcDistance } from './gameState';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';

export type RearmEvent = { kind: 'rearmed'; structureId: number; dumpId: number; ammo: AmmoType; left: number };

const OF_ROUND: Record<TankRound, 'tankHe' | 'tankSolid'> = { he: 'tankHe', solid: 'tankSolid' };
type Main = NonNullable<NonNullable<StructureState['seats']>[number]['main']>;

/** A standing crate holding any `type` round, or any round at all. */
export function dumpHolds(s: Pick<StructureState, 'type' | 'health' | 'supply'>, type?: AmmoType): boolean {
  const a = s.supply?.ammo;
  if (!a || !standingWithJob(s, 'supply')) return false;
  return type ? (a[type] ?? 0) > 0 : Object.values(a).some(n => (n ?? 0) > 0);
}

/** Step 5: a supply truck's bed, while it is a dump — the truck standing and parked (under MUNITIONS.stopped of the walk's speed) — else null. */
export function bedOf(s: StructureState, walkSpeed: number): Partial<Record<AmmoType, number>> | null {
  if (!s.ammo || s.health <= 0 || !cargoOnly(s) || !drives(s) || Math.abs(s.drive?.speed ?? 0) >= MUNITIONS.stopped * walkSpeed) return null;
  return s.ammo;
}

/** The tank's rounds aboard by type (the one in the gun counted with its kind) against its cap, the stowage it started with. */
export function tankShort(main: Main, cap: { he: number; solid: number }): Record<TankRound, number> {
  return { he: cap.he - main.he - (main.loaded === 'he' ? 1 : 0), solid: cap.solid - main.solid - (main.loaded === 'solid' ? 1 : 0) };
}

/** One store a vehicle rearms: its round type, its cap, how many it is short of it, and the vehicle with one more stowed. */
export interface RearmStore { type: keyof typeof MUNITIONS.pace; cap: number; short: number; add: (s: StructureState) => StructureState }   // (a type some vehicle stows: not the salvo's light shell)

const seatWith = (s: StructureState, k: number, f: (seat: NonNullable<StructureState['seats']>[number]) => NonNullable<StructureState['seats']>[number]): StructureState =>
  ({ ...s, seats: s.seats!.map((x, j) => (j === k ? f(x) : x)) });

/** The stores `s` rearms, in order (the tank's explosive before its solid, the seats', then the mortar's). */
export function rearmStores(s: StructureState, config: Pick<GameConfig, 'tankStowage' | 'mountStore' | 'mortarStore'>): RearmStore[] {
  const out: RearmStore[] = [];
  (s.seats ?? []).forEach((seat, k) => {
    if (seat.main) {
      const cap = config.tankStowage ?? TANK_GUN.stowage, short = tankShort(seat.main, cap);
      for (const r of ['he', 'solid'] as const) out.push({ type: OF_ROUND[r], cap: cap[r], short: short[r], add: v => seatWith(v, k, x => ({ ...x, main: { ...x.main!, [r]: x.main![r] + 1 } })) });
    }
    if (seat.launcher) {
      const L = MOUNT_LAUNCHERS[seat.launcher], store = config.mountStore ?? L.store;
      const spare = seat.spare ?? store, belt = seat.belt ?? L.magazine;
      out.push({ type: seat.launcher, cap: L.magazine + store, short: L.magazine + store - belt - spare, add: v => seatWith(v, k, x => ({ ...x, spare: (x.spare ?? store) + 1 })) });
    }
  });
  if (s.mortar) out.push({ type: 'mortarBomb', cap: config.mortarStore ?? MORTAR.store, short: (config.mortarStore ?? MORTAR.store) - s.mortar.rounds, add: v => ({ ...v, mortar: { ...v.mortar!, rounds: v.mortar!.rounds + 1 } }) });
  return out;
}

/** The store `stores` take next from `dump`: the one furthest below its cap of a type the dump holds (the first on a tie), or null. */
function nextStore(stores: readonly RearmStore[], ammo: Partial<Record<AmmoType, number>>): RearmStore | null {
  let best: RearmStore | null = null;
  for (const st of stores) if (st.short > 0 && (ammo[st.type] ?? 0) > 0 && (!best || st.short > best.short)) best = st;
  return best;
}

/**
 * The dump `s` rearms from now, by the rule `stepRearm` applies: the nearest in reach (any side's — 242; the lower index on a
 * tie) holding a round the vehicle is short of — a crate, or a parked supply truck's bed (step 5, `bed`) — with the store it
 * takes next; null when it is not parked, is full, or no dump in reach holds what it lacks. 69.31: the dump's arm is drawn from it.
 */
export function rearmDumpOf(structures: readonly StructureState[], s: StructureState, state: Pick<GameState, 'planets' | 'config'>, ps: { walkSpeed: number }, person: number): { dump: StructureState; store: RearmStore; bed: boolean } | null {
  const planet = state.planets[s.planetIndex];
  if (!s.drive || s.health <= 0 || !planet || Math.abs(s.drive.speed) >= MUNITIONS.stopped * ps.walkSpeed) return null;
  const stores = rearmStores(s, state.config);
  if (!stores.some(x => x.short > 0)) return null;
  let found: { dump: StructureState; store: RearmStore; bed: boolean } | null = null, best = Infinity;
  for (const d of structures) {
    if (d.planetIndex !== s.planetIndex || d.id === s.id) continue;
    const truck = dumpHolds(d) ? null : bedOf(d, ps.walkSpeed), ammo = truck ?? (dumpHolds(d) ? d.supply!.ammo! : null);
    if (!ammo) continue;
    const next = nextStore(stores, ammo);
    if (!next) continue;
    const at = arcDistance(s.surfaceDir, d.surfaceDir, planet.radius);
    if (at <= (truck ? MUNITIONS.truckReach : MUNITIONS.reach) * person && at < best) { best = at; found = { dump: d, store: next, bed: !!truck }; }
  }
  return found;
}

/**
 * One beat of the rearm (`dt` seconds since the last): every parked vehicle short of a round with a dump in reach moves
 * toward its next round and takes it when due. Null when nothing changed.
 */
export function stepRearm(state: Pick<GameState, 'structures' | 'planets' | 'config'>, dt: number): { structures: StructureState[]; events: RearmEvent[] } | null {
  let out: StructureState[] | null = null;
  const events: RearmEvent[] = [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  for (let i = 0; i < state.structures.length; i++) {
    const s = (out ?? state.structures)[i];
    if (!s.drive && s.rearm === undefined) continue;   // (a vehicle: it drives)
    const found = rearmDumpOf(out ?? state.structures, s, state, ps, person);
    const dump = found?.dump ?? null, store = found?.store ?? null, bed = found?.bed ?? false;
    if (!dump || !store) {
      if (s.rearm !== undefined) { const { rearm: _gone, ...rest } = s; (out ??= state.structures.slice())[i] = rest; }   // (driven off, full, or the dump dry: the round under way dropped)
      continue;
    }
    const t = (s.rearm ?? 0) + dt;
    if (t + 1e-9 < MUNITIONS.pace[store.type]) { (out ??= state.structures.slice())[i] = { ...s, rearm: t }; continue; }
    const { rearm: _done, ...rest } = s, left = ((bed ? dump.ammo! : dump.supply!.ammo!)[store.type] ?? 0) - 1;
    out ??= state.structures.slice();
    out[i] = store.add(rest);
    const j = out.findIndex(x => x.id === dump!.id);
    out[j] = bed ? { ...dump, ammo: { ...dump.ammo, [store.type]: left } } : { ...dump, supply: { ...dump.supply!, ammo: { ...dump.supply!.ammo, [store.type]: left } } };
    events.push({ kind: 'rearmed', structureId: s.id, dumpId: dump.id, ammo: store.type, left });
  }
  return out ? { structures: out, events } : null;
}

/** Step 3: a side's own crates' rounds of `type` (its base's: the team crates, not a point's, nor another side's) — the base's stock. */
export function baseShells(state: { structures: readonly StructureState[] }, team: number, type: AmmoType = 'lightShell'): number {
  let n = 0;
  for (const s of state.structures) if (s.supply && s.supply.site === undefined && s.supply.teamIndex === team && dumpHolds(s, type)) n += s.supply.ammo![type]!;
  return n;
}

/** Step 3: `count` rounds of `type` taken off a side's own crates, the lowest id first (as far as they go). */
export function spendBaseShells(structures: readonly StructureState[], team: number, count: number, type: AmmoType = 'lightShell'): StructureState[] {
  let left = count;
  return structures.map(s => {
    if (left <= 0 || !s.supply || s.supply.site !== undefined || s.supply.teamIndex !== team || !dumpHolds(s, type)) return s;
    const take = Math.min(left, s.supply.ammo![type]!);
    left -= take;
    return { ...s, supply: { ...s.supply, ammo: { ...s.supply.ammo, [type]: s.supply.ammo![type]! - take } } };
  });
}

export type BedEvent = { kind: 'bedLoaded'; structureId: number; team: number; ammo: Partial<Record<AmmoType, number>> };

/**
 * Step 5: every supply truck standing at its home (`atHome`) tops its bed up to MUNITIONS.truck by type from its side's base
 * crates (`spendBaseShells`' rule: its own crates, the lowest id first, as far as they go). Null when nothing changed.
 */
export function loadBeds(state: Pick<GameState, 'structures' | 'players'>): { structures: StructureState[]; events: BedEvent[] } | null {
  let out: StructureState[] | null = null;
  const events: BedEvent[] = [];
  for (let i = 0; i < state.structures.length; i++) {
    const s = (out ?? state.structures)[i];
    if (!s.atHome || s.health <= 0 || !cargoOnly(s) || !drives(s)) continue;
    const team = state.players.find(p => p.id === s.ownerId)?.teamIndex;
    if (team === undefined) continue;
    let all: readonly StructureState[] = out ?? state.structures, bed: Partial<Record<AmmoType, number>> = { ...(s.ammo ?? {}) };
    const took: Partial<Record<AmmoType, number>> = {};
    for (const type of Object.keys(MUNITIONS.truck) as Array<keyof typeof MUNITIONS.truck>) {
      const want = MUNITIONS.truck[type] - (bed[type] ?? 0), have = baseShells({ structures: all }, team, type), take = Math.min(want, have);
      if (take <= 0) continue;
      all = spendBaseShells(all, team, take, type);
      bed = { ...bed, [type]: (bed[type] ?? 0) + take };
      took[type] = take;
    }
    if (!Object.keys(took).length) continue;
    const next = all.slice();
    next[i] = { ...s, ammo: bed };
    out = next;
    events.push({ kind: 'bedLoaded', structureId: s.id, team, ammo: took });
  }
  return out ? { structures: out, events } : null;
}
